// src/components/BillCustomer/billApi.js
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Bill API uses AWS API Gateway (as per documentation)
// Note: If this doesn't work, try https://api.innoworx.ai/Stage
const API_BASE_URL = 'https://x1f3lmpgxa.execute-api.ap-south-1.amazonaws.com/Stage';

/**
 * Helper function to make authenticated API requests
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  try {
    // Get authentication token
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      throw new Error('No authentication token available');
    }

    // Trim token to remove any whitespace that might cause header parsing issues
    const token = idToken.trim();

    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    console.log(`Bill API: Making ${method} request to: ${url}`);
    console.log('Bill API: Authorization header format:', `Bearer ${token.substring(0, 20)}...`);
    if (body) {
      console.log('Bill API: Request payload:', body);
    }

    let response;
    try {
      response = await fetch(url, options);
    } catch (fetchError) {
      // Handle CORS and network errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
        const corsError = new Error('CORS Error: Unable to connect to the API. This may be a preflight (OPTIONS) request issue or backend CORS configuration issue.');
        corsError.isCorsError = true;
        corsError.isPreflightIssue = true;
        corsError.status = 0;
        corsError.method = method;
        corsError.endpoint = endpoint;
        console.error('Bill API: CORS/Preflight Error Details:', {
          method,
          endpoint,
          url,
          error: fetchError.message,
          suggestion: 'Backend needs to handle OPTIONS requests and return proper CORS headers',
        });
        throw corsError;
      }
      throw fetchError;
    }

    if (!response.ok) {
      // Read response as text first, then try to parse as JSON
      const errorText = await response.text().catch(() => '');
      let errorData = {};
      
      if (errorText) {
        try {
          errorData = JSON.parse(errorText);
        } catch {
          // If not JSON, use the text as the error message
          errorData = { message: errorText, error: errorText };
        }
      }
      
      console.error(
        `Bill API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );
      console.error('Bill API: Full error response:', errorText);
      console.error('Bill API: Authorization header sent:', `Bearer ${token.substring(0, 20)}...`);

      // Extract error message from various possible response formats
      let errorMessage = 'Unknown error';
      if (errorData.body?.error) {
        errorMessage = errorData.body.error;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (typeof errorText === 'string' && errorText) {
        errorMessage = errorText; // Fallback to raw text if not parsable JSON
      }

      const error = new Error(`API Error: ${response.status} - ${errorMessage}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_bill',
        `Bill API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[billApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const data = await response.json();
    console.log('Bill API: Response:', data);
    return data;
  } catch (error) {
    console.error(`Bill API: Error in ${method} ${endpoint}:`, error);
    
    // Log network/fetch errors to logging service
    if (!error.isCorsError && error.status) {
      // Already logged above for response errors
    } else {
      loggingService.logError(
        'api_error_bill',
        `Bill API network/fetch error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: error.name || 'NetworkError',
          isCorsError: error.isCorsError || false,
        }
      ).catch(logError => {
        console.warn('[billApi] Failed to log error:', logError);
      });
    }
    
    if (error.isCorsError) {
      throw error;
    }
    if (error.status) {
      throw error;
    }
    throw new Error(error.message || 'Network error');
  }
};

/**
 * Clean object by removing undefined, null, and empty string values
 */
const cleanObject = (obj) => {
  if (obj === null || obj === undefined) {
    return undefined;
  }
  if (Array.isArray(obj)) {
    return obj.map(cleanObject).filter((item) => item !== undefined);
  }
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = cleanObject(value);
      if (cleanedValue !== undefined && cleanedValue !== null && cleanedValue !== '') {
        cleaned[key] = cleanedValue;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }
  return obj;
};

/**
 * Bill API service
 */
export const billApi = {
  /**
   * Create a new bill
   * @param {Object} billData - Bill data including order_id, site_id, and items
   * @returns {Promise<Object>} Created bill
   */
  createBill: async (billData) => {
    try {
      console.log('Bill API: Raw bill data received:', JSON.stringify(billData, null, 2));
      
      // Validate required fields first
      if (!billData.order_id) {
        throw new Error('order_id is required');
      }
      if (!billData.site_id) {
        throw new Error('site_id is required');
      }
      if (!billData.items || !Array.isArray(billData.items) || billData.items.length === 0) {
        throw new Error('At least one item is required');
      }
      
      // Don't clean required fields - ensure order_id, site_id, and items are always present
      const cleanedData = {
        order_id: billData.order_id,
        site_id: billData.site_id,
        items: billData.items || [],
        ...cleanObject(billData), // Clean optional fields
      };
      
      // Ensure items array is not empty and has required fields
      if (!cleanedData.items || cleanedData.items.length === 0) {
        throw new Error('At least one item is required');
      }
      
      // Validate and process each item - ensure all required fields are present and properly typed
      const processedItems = cleanedData.items.map((item, index) => {
        if (!item.name || !item.name.trim()) {
          throw new Error(`Item ${index + 1}: name is required`);
        }
        const quantity = typeof item.quantity === 'number' ? item.quantity : parseFloat(item.quantity);
        if (isNaN(quantity) || quantity <= 0) {
          throw new Error(`Item ${index + 1}: quantity must be a positive number`);
        }
        const pricePerUnit = typeof item.price_per_unit === 'number' ? item.price_per_unit : parseFloat(item.price_per_unit);
        if (isNaN(pricePerUnit) || pricePerUnit < 0) {
          throw new Error(`Item ${index + 1}: price_per_unit must be a non-negative number`);
        }
        
        const itemObj = {
          name: item.name.trim(),
          quantity: quantity,
          price_per_unit: pricePerUnit,
        };
        
        // Add optional fields only if they have values
        // Calculate subtotal if not provided, or use provided value (fix floating point precision)
        if (item.subtotal !== undefined && item.subtotal !== null) {
          const subtotal = typeof item.subtotal === 'number' ? item.subtotal : parseFloat(item.subtotal);
          if (!isNaN(subtotal)) {
            // Fix floating point precision issues (e.g., 499.79999999999995 -> 499.80)
            itemObj.subtotal = parseFloat(subtotal.toFixed(2));
          }
        } else {
          // Auto-calculate subtotal if not provided
          const calculatedSubtotal = quantity * pricePerUnit;
          itemObj.subtotal = parseFloat(calculatedSubtotal.toFixed(2));
        }
        if (item.unit && item.unit.trim()) {
          itemObj.unit = item.unit.trim();
        }
        if (item.notes && item.notes.trim()) {
          itemObj.notes = item.notes.trim();
        }
        
        return itemObj;
      });
      
      cleanedData.items = processedItems;
      
      console.log('Bill API: Final cleaned payload:', JSON.stringify(cleanedData, null, 2));
      console.log('Bill API: Payload validation:', {
        has_order_id: !!cleanedData.order_id,
        has_site_id: !!cleanedData.site_id,
        items_count: cleanedData.items?.length || 0,
        items_valid: cleanedData.items?.every(item => 
          item.name && 
          typeof item.quantity === 'number' && item.quantity > 0 &&
          typeof item.price_per_unit === 'number' && item.price_per_unit >= 0
        ),
        subtotal_type: typeof cleanedData.subtotal,
        total_type: typeof cleanedData.total,
        customer_id: cleanedData.customer_id || null,
        customer_object: cleanedData.customer || null,
      });
      
      const response = await makeApiRequest('/billing/bills', 'POST', cleanedData);
      
      // Log API event for creating bill
      loggingService.logInfo(
        'api_success_bill',
        `Successfully created bill for order ${billData.order_id}`,
        {
          endpoint: '/billing/bills',
          method: 'POST',
          orderId: billData.order_id,
          siteId: billData.site_id,
          itemsCount: cleanedData.items?.length || 0,
        }
      ).catch(logError => {
        console.warn('[billApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        bill: response.bill,
        next_steps: response.next_steps || null,
      };
    } catch (error) {
      console.error('Bill API: Error creating bill:', error);
      return {
        success: false,
        error: error.message || 'Failed to create bill',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * List bills with optional filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} [params.limit] - Number of items per page (default: 20, max: 100)
   * @param {string} [params.last_key] - Pagination token from previous response
   * @param {string} [params.start_date] - Start date filter (YYYY-MM-DD or ISO format)
   * @param {string} [params.end_date] - End date filter (YYYY-MM-DD or ISO format)
   * @param {string} [params.site_id] - Filter by site ID
   * @param {string} [params.customer_id] - Filter by customer ID
   * @param {string} [params.table_number] - Filter by table number
   * @param {string} [params.order_id_contains] - Search in order_id (case-insensitive)
   * @param {string} [params.bill_number_contains] - Search in bill_number (case-insensitive)
   * @returns {Promise<Object>} Bills list with pagination info
   */
  listBills: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params.last_key !== undefined && params.last_key !== null) {
        queryParams.append('last_key', params.last_key);
      }
      if (params.start_date) {
        queryParams.append('start_date', params.start_date);
      }
      if (params.end_date) {
        queryParams.append('end_date', params.end_date);
      }
      if (params.site_id) {
        queryParams.append('site_id', params.site_id);
      }
      if (params.customer_id) {
        queryParams.append('customer_id', params.customer_id);
      }
      if (params.table_number) {
        queryParams.append('table_number', params.table_number);
      }
      if (params.order_id_contains) {
        queryParams.append('order_id_contains', params.order_id_contains);
      }
      if (params.bill_number_contains) {
        queryParams.append('bill_number_contains', params.bill_number_contains);
      }

      const queryString = queryParams.toString();
      const endpoint = `/billing/bills${queryString ? `?${queryString}` : ''}`;

      console.log(`Bill API: Listing bills: ${endpoint}`);
      const response = await makeApiRequest(endpoint, 'GET');
      
      // Log API event for listing bills
      loggingService.logInfo(
        'api_success_bill',
        `Successfully listed bills`,
        {
          endpoint: '/billing/bills',
          method: 'GET',
          count: response.count || 0,
          hasMore: response.has_more || false,
          ...params,
        }
      ).catch(logError => {
        console.warn('[billApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        bills: response.bills || [],
        count: response.count || 0,
        has_more: response.has_more || false,
        last_key: response.last_key || null,
      };
    } catch (error) {
      console.error('Bill API: Error listing bills:', error);
      return {
        success: false,
        error: error.message || 'Failed to list bills',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Get a single bill by ID
   * @param {string} billId - Bill ID
   * @param {Object} params - Query parameters (include_download_url)
   * @returns {Promise<Object>} Bill details with optional download URL
   */
  getBill: async (billId, params = {}) => {
    try {
      if (!billId) {
        throw new Error('Bill ID is required');
      }
      
      const queryParams = new URLSearchParams();
      if (params.include_download_url === true || params.include_download_url === 'true') {
        queryParams.append('include_download_url', 'true');
      }

      const queryString = queryParams.toString();
      const endpoint = `/billing/bills/${billId}${queryString ? `?${queryString}` : ''}`;

      const response = await makeApiRequest(endpoint, 'GET');
      
      // Log API event for getting bill
      loggingService.logInfo(
        'api_success_bill',
        `Successfully fetched bill ${billId}`,
        {
          endpoint: `/billing/bills/${billId}`,
          method: 'GET',
          billId,
          includeDownloadUrl: params.include_download_url || false,
        }
      ).catch(logError => {
        console.warn('[billApi] Failed to log success event:', logError);
      });
      
      return {
        success: true,
        data: response,
        bill: response.bill,
        download_url: response.download_url || null,
        download_url_expires_in: response.download_url_expires_in || null,
      };
    } catch (error) {
      console.error('Bill API: Error getting bill:', error);
      return {
        success: false,
        error: error.message || 'Failed to get bill',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Get presigned URL for bill document upload
   * @param {string} billId - Bill ID
   * @param {Object} params - Request body (content_type, file_name)
   * @returns {Promise<Object>} Presigned POST data
   */
  presignBillDocumentUpload: async (billId, params = {}) => {
    try {
      if (!billId) {
        throw new Error('Bill ID is required');
      }
      
      const requestBody = cleanObject({
        content_type: params.content_type || 'application/pdf',
        file_name: params.file_name,
      });

      const response = await makeApiRequest(
        `/billing/bills/${billId}/presign`,
        'POST',
        requestBody
      );
      return {
        success: true,
        data: response,
        bill_id: response.bill_id,
        bucket: response.bucket,
        object_key: response.object_key,
        content_type: response.content_type,
        presigned_post: response.presigned_post,
        expires_in: response.expires_in || 900,
      };
    } catch (error) {
      console.error('Bill API: Error getting presigned URL:', error);
      return {
        success: false,
        error: error.message || 'Failed to get presigned URL',
        status: error.status,
        responseData: error.responseData,
      };
    }
  },

  /**
   * Upload document to S3 using presigned POST
   * @param {Object} presignedPost - Presigned POST data from presignBillDocumentUpload
   * @param {File|Blob} file - File to upload
   * @returns {Promise<Object>} Upload result
   */
  uploadBillDocument: async (presignedPost, file) => {
    try {
      if (!presignedPost || !presignedPost.url || !presignedPost.fields) {
        throw new Error('Invalid presigned POST data');
      }
      if (!file) {
        throw new Error('File is required');
      }

      const formData = new FormData();
      Object.entries(presignedPost.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const response = await fetch(presignedPost.url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`S3 upload failed: ${response.status} - ${errorText}`);
      }

      return {
        success: true,
        message: 'Document uploaded successfully',
      };
    } catch (error) {
      console.error('Bill API: Error uploading document:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload document',
      };
    }
  },
};

export default billApi;

